/**
 * Identificativi e ticker collegati (SPEC-016 blocco 6): una riga per ogni
 * identificativo con il suo uso, la borsa e lo stato del mapping. «Risolvi
 * manualmente» riusa le mutation di `features/watchlist/mapping-api.ts`
 * (F-040): stessa rotta, stesso comportamento del dialog della watchlist.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api-client';
import { Alert, Badge, Button, Field, Modal, TableWrap } from '@/ui';
import { formatExchangeCode } from '@/features/watchlist/openfigi-exchange-codes';
import { useSetMapping } from '@/features/watchlist/mapping-api';
import { BrokerTickerField } from '@/features/connectors/BrokerTickerField';
import { instrumentKeys, type InstrumentDetail } from './api';

type Row = {
  value: string;
  kind: string;
  usedFor: string;
  venue: string;
  status: { label: string; tone: 'neutral' | 'positive' | 'negative' | 'accent' | 'warning' } | null;
};

function buildRows(detail: InstrumentDetail): Row[] {
  const { identity, mapping, market } = detail;
  const rows: Row[] = [];

  if (mapping.providerSymbol) {
    rows.push({
      value: mapping.providerSymbol,
      kind: 'Ticker Yahoo',
      usedFor: 'Quotazioni EOD e intraday (provider primario)',
      venue: market ? `${market.name} · ${market.mic}` : (mapping.marketMic ?? '—'),
      status:
        mapping.mappingStatus === 'resolved'
          ? { label: 'risolto', tone: 'positive' }
          : { label: mapping.mappingStatus ?? 'da risolvere', tone: 'warning' },
    });
  }
  if (identity.trading212Ticker) {
    rows.push({
      value: identity.trading212Ticker,
      kind: 'Ticker Trading 212',
      usedFor: 'Ordini reali e sincronizzazione posizioni',
      venue: 'Trading 212',
      status: { label: 'collegato', tone: 'accent' },
    });
  }
  if (identity.isin) {
    rows.push({
      value: identity.isin,
      kind: 'ISIN',
      usedFor: 'Chiave d’anagrafica, incrocio import PDF/CSV',
      venue: '—',
      status: { label: 'anagrafica', tone: 'neutral' },
    });
  }
  if (identity.figi) {
    rows.push({
      value: identity.figi,
      kind: 'FIGI OpenFIGI',
      usedFor: 'Mapping ISIN → ticker',
      venue: identity.exchange ? formatExchangeCode(identity.exchange) : '—',
      status: { label: 'scelto', tone: 'positive' },
    });
  }
  for (const candidate of mapping.mappingCandidates ?? []) {
    rows.push({
      value: candidate.ticker,
      kind: 'Candidato OpenFIGI',
      usedFor: 'Non usato — in attesa di scelta',
      venue: candidate.exchange ? formatExchangeCode(candidate.exchange) : '—',
      status: { label: 'da scegliere', tone: 'warning' },
    });
  }
  return rows;
}

export function IdentifiersTable({ detail }: { detail: InstrumentDetail }) {
  const [resolving, setResolving] = useState(false);
  const [linkingBroker, setLinkingBroker] = useState(false);
  const rows = buildRows(detail);

  return (
    <>
      <div className="identifiers__toolbar">
        <Button variant="secondary" onClick={() => setResolving(true)}>
          Risolvi manualmente
        </Button>
        {/* Associare un ticker del broker e' una MAPPATURA, non un ordine:
            finora si poteva fare solo dentro il modal di invio ordine, cioe'
            passando da un gesto di trading per sistemare un dato. */}
        <Button variant="secondary" onClick={() => setLinkingBroker(true)}>
          {detail.identity.trading212Ticker ? 'Cambia ticker broker' : 'Collega ticker broker'}
        </Button>
      </div>
      {linkingBroker && (
        <Modal title={`Ticker Trading 212 — ${detail.identity.name}`} onClose={() => setLinkingBroker(false)}>
          <BrokerTickerField
            instrumentId={detail.identity.id}
            instrumentName={detail.identity.name}
            currentTicker={detail.identity.trading212Ticker}
            // Il simbolo di quotazione e' il punto di partenza piu' utile: il
            // catalogo del broker si cerca per ticker o per nome.
            defaultQuery={detail.identity.symbol ?? detail.identity.name}
          />
        </Modal>
      )}
      {rows.length === 0 ? (
        <p className="identifiers__empty">Nessun identificativo risolto per questo titolo.</p>
      ) : (
        <TableWrap>
          <table className="table">
            <thead>
              <tr>
                <th>Identificativo</th>
                <th>Tipo</th>
                <th>Usato per</th>
                <th>Borsa / venue</th>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.kind}:${row.value}`}>
                  <td className="numeric">{row.value}</td>
                  <td>{row.kind}</td>
                  <td>{row.usedFor}</td>
                  <td>{row.venue}</td>
                  <td>{row.status && <Badge tone={row.status.tone}>{row.status.label}</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
      {detail.mapping.mappingUpdatedAt && (
        <p className="identifiers__footnote">
          Mapping aggiornato il {new Date(detail.mapping.mappingUpdatedAt).toLocaleDateString('it-IT')}
          {detail.mapping.mappingCandidates
            ? ` · ${detail.mapping.mappingCandidates.length} candidati OpenFIGI persistiti`
            : ''}
          {' '}· il mercato è derivato dal suffisso del ticker Yahoo, non da OpenFIGI.
        </p>
      )}
      {resolving && (
        <ManualResolveModal instrumentId={detail.identity.id} onClose={() => setResolving(false)} />
      )}
    </>
  );
}

function ManualResolveModal({ instrumentId, onClose }: { instrumentId: string; onClose: () => void }) {
  const [ticker, setTicker] = useState('');
  const queryClient = useQueryClient();
  const setMapping = useSetMapping(instrumentId);

  const submit = () => {
    setMapping.mutate(ticker.trim().toUpperCase(), {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: instrumentKeys.detail(instrumentId) });
        onClose();
      },
    });
  };

  return (
    <Modal title="Risolvi il ticker manualmente" onClose={onClose}>
      <p>
        Inserisci il ticker Yahoo Finance completo di suffisso (es. <code>LDO.MI</code> per Milano,
        <code> TSCO.L</code> per Londra). Un ticker sbagliato degrada in «nessuna quotazione», mai in
        un dato falso.
      </p>
      <Field
        label="Ticker Yahoo"
        value={ticker}
        onChange={(event) => setTicker(event.target.value)}
        placeholder="es. LDO.MI"
      />
      {setMapping.isError && (
        <Alert variant="error" title="Risoluzione rifiutata">
          {setMapping.error instanceof ApiError ? setMapping.error.message : 'Errore imprevisto'}
        </Alert>
      )}
      <div className="modal-actions">
        <Button variant="secondary" onClick={onClose}>
          Annulla
        </Button>
        <Button onClick={submit} disabled={ticker.trim().length === 0} loading={setMapping.isPending}>
          Salva ticker
        </Button>
      </div>
    </Modal>
  );
}
